// Content & safety moderation queue (spec §6.6), backed by the fix-round-1
// addition `admin-moderation` (list from content_reports_queue, resolve via
// admin_resolve_content_report). Verified-source badges, throttle controls
// and the emergency helpline table editor are deferred follow-up work — see
// portal/notes/T23.md — this screen covers the report queue itself.
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';
import { adminMutate } from '@/lib/api/adminMutate';
import { toReason } from '@/lib/api/reason';
import { cancelThenInvalidate } from '@/lib/query/refresh';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/content')({
  component: ContentPage,
});

interface Report {
  id: string;
  created_at: string;
  status: 'pending' | 'actioned' | 'dismissed';
  target_type: string;
  reason: string;
  detail: string | null;
  reporter_name: string | null;
  author_name: string | null;
  content: string | null;
  reviewed_at: string | null;
  reviewer_note: string | null;
  review_id: string | null;
}

interface Cursor {
  createdAt: string;
  id: string;
}

interface ListResponse {
  reports: Report[];
  nextCursor: Cursor | null;
  direction: 'asc' | 'desc';
}

const STATUS_TABS = ['pending', 'actioned', 'dismissed'] as const;

// Road reports submitted in the app start 'pending' and stay invisible to
// other users until approved here (BUGS 199, 20261013000004).
interface PendingRoadUpdate {
  id: string;
  created_at: string;
  road_name: string;
  severity: string;
  category: string;
  description: string;
  image_url: string | null;
  distance_marker: string | null;
  latitude: number | null;
  longitude: number | null;
  profiles: { display_name: string | null } | null;
}

function RoadApprovals() {
  const { supabase } = useAuth();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-moderation', 'road_list'],
    queryFn: () =>
      callEdgeFunction<{ roadUpdates: PendingRoadUpdate[] }>(supabase, 'admin-moderation', { action: 'road_list' }),
  });
  const decide = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'approved' | 'rejected' }) => {
      const r = toReason(reason);
      if (!r) throw new Error('A reason of at least 10 characters is required.');
      return adminMutate<{ outcome: string }>(
        supabase,
        'admin-moderation',
        { action: 'road_decide', roadUpdateId: id, decision },
        r,
      );
    },
    onSuccess: () => {
      setReason('');
      cancelThenInvalidate(queryClient, { queryKey: ['admin-moderation', 'road_list'] });
    },
  });
  const items = data?.roadUpdates ?? [];

  return (
    <div className="flex flex-col gap-md">
      <h2 className="text-h2 font-bold text-ink">
        Road reports awaiting approval{items.length ? ` (${items.length})` : ''}
      </h2>
      <p className="text-caption text-ink-2">
        Not visible to other travellers until approved. Approving notifies people who reported on the same road.
      </p>
      {isLoading ? <PageLoading /> : null}
      {error ? <p className="text-body text-danger">{(error as Error).message}</p> : null}
      {!isLoading && !error && items.length === 0 ? (
        <p className="rounded-card border border-border bg-surface p-lg text-center text-body text-ink-2">
          No road reports waiting.
        </p>
      ) : null}
      {items.length > 0 ? (
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <label className="text-caption font-semibold text-ink-2" htmlFor="road-reason">
            Reason (required to approve or reject)
          </label>
          <textarea
            id="road-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-xs min-h-[52px] w-full rounded-button border border-border bg-surface p-sm text-body text-ink"
          />
        </div>
      ) : null}
      {items.map((u) => (
        <div key={u.id} className="rounded-card border border-border bg-surface p-lg shadow-card">
          <div className="flex items-center justify-between gap-sm">
            <p className="text-body font-semibold text-ink">
              {u.road_name} · {u.severity} · {u.category}
            </p>
            <span className="text-caption text-ink-2">{new Date(u.created_at).toLocaleString()}</span>
          </div>
          <p className="mt-xs text-caption text-ink-2">
            By {u.profiles?.display_name ?? 'unknown'}
            {u.distance_marker ? ` · ${u.distance_marker}` : ''}
            {u.latitude != null && u.longitude != null ? (
              <>
                {' · '}
                <a
                  className="underline"
                  href={`https://www.google.com/maps?q=${u.latitude},${u.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  map
                </a>
              </>
            ) : null}
          </p>
          <p className="mt-sm text-body text-ink">{u.description}</p>
          {u.image_url ? (
            <img src={u.image_url} alt="" className="mt-sm max-h-64 rounded-button object-cover" />
          ) : null}
          <div className="mt-md flex gap-sm">
            <button
              type="button"
              disabled={decide.isPending || !toReason(reason)}
              onClick={() => decide.mutate({ id: u.id, decision: 'approved' })}
              className="rounded-button bg-canyon px-md py-sm text-body font-semibold text-white disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={decide.isPending || !toReason(reason)}
              onClick={() => decide.mutate({ id: u.id, decision: 'rejected' })}
              className="rounded-button border border-border px-md py-sm text-body text-ink disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        </div>
      ))}
      {decide.isError ? <p className="text-body text-danger">{(decide.error as Error).message}</p> : null}
    </div>
  );
}

// Fix round 2: pending sorts OLDEST first server-side (admin-moderation
// fixes the direction by status) — an overdue report can never sit buried
// behind newer ones on page 1. "Load more" pages with a real
// (created_at, id) keyset so a same-timestamp tie can't skip/repeat a row.
function useReports(status: string, cursor: Cursor | null) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-moderation', 'list', status, cursor?.createdAt, cursor?.id],
    queryFn: () =>
      callEdgeFunction<ListResponse>(supabase, 'admin-moderation', {
        action: 'list',
        status,
        limit: 50,
        ...(cursor ? { cursorCreatedAt: cursor.createdAt, cursorId: cursor.id } : {}),
      }),
  });
}

function ContentPage() {
  const { supabase } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<(typeof STATUS_TABS)[number]>('pending');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [hideReview, setHideReview] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState('');
  const [pages, setPages] = useState<Report[][]>([]);
  const [cursor, setCursor] = useState<Cursor | null>(null);

  const { data, isLoading, error } = useReports(status, cursor);

  // Pages accumulate across "Load more" clicks. A brand-new page (cursor
  // just changed and this response matches it) is appended once; switching
  // status/tab resets both `pages` and `cursor` via handleStatusChange.
  const [lastAppliedCursorKey, setLastAppliedCursorKey] = useState<string | null>(null);
  const cursorKey = `${status}:${cursor?.createdAt ?? ''}:${cursor?.id ?? ''}`;
  useEffect(() => {
    if (data && cursorKey !== lastAppliedCursorKey) {
      setPages((prev) => (cursor ? [...prev, data.reports] : [data.reports]));
      setLastAppliedCursorKey(cursorKey);
    }
  }, [data, cursor, cursorKey, lastAppliedCursorKey]);

  const reports = pages.flat();
  const nextCursor = data?.nextCursor ?? null;

  function handleStatusChange(next: (typeof STATUS_TABS)[number]) {
    setStatus(next);
    setCursor(null);
    setPages([]);
    setLastAppliedCursorKey(null);
  }

  const resolveMutation = useMutation({
    mutationFn: async ({ reportId, next }: { reportId: string; next: 'actioned' | 'dismissed' }) => {
      const r = toReason(reason);
      if (!r) throw new Error('A reason of at least 10 characters is required.');
      return adminMutate<{ outcome: string }>(
        supabase,
        'admin-moderation',
        {
          action: 'resolve',
          reportId,
          status: next,
          reviewerNote: notes[reportId]?.trim() || undefined,
          hideReview: hideReview[reportId] === true,
        },
        r,
      );
    },
    onSuccess: () => {
      setReason('');
      // Reset to page 1 of the current tab and let the effect above rebuild
      // `pages` from the fresh fetch — a resolved report should disappear
      // from "pending" immediately, not linger in the accumulated pages.
      setCursor(null);
      setPages([]);
      setLastAppliedCursorKey(null);
      cancelThenInvalidate(queryClient, { queryKey: ['admin-moderation', 'list'] });
    },
  });

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Content &amp; safety</h1>
        <p className="mt-sm text-body text-ink-2">
          Moderation queue for reported reviews, road updates, messages, businesses and users. Verified-source
          badges, throttle controls and the emergency helpline editor are follow-up work (see PLAN.md/notes).
        </p>
      </div>

      <RoadApprovals />

      <h2 className="text-h2 font-bold text-ink">Reported content</h2>
      <div className="flex gap-sm">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => handleStatusChange(s)}
            className={`rounded-button px-md py-xs text-caption font-semibold ${
              status === s ? 'bg-canyon text-white' : 'border border-border text-ink'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <label className="text-caption font-semibold text-ink-2" htmlFor="moderation-reason">
          Reason (required for every resolution below)
        </label>
        <textarea
          id="moderation-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-xs min-h-[52px] w-full rounded-button border border-border bg-surface p-sm text-body text-ink"
        />
      </div>

      {status === 'pending' ? (
        <p className="text-caption text-ink-2">Oldest first — nothing overdue is buried behind newer reports.</p>
      ) : null}

      {isLoading ? <PageLoading /> : null}
      {error ? <p className="text-body text-danger">{(error as Error).message}</p> : null}
      {!isLoading && !error && reports.length === 0 ? (
        <p className="rounded-card border border-border bg-surface p-xl text-center text-body text-ink-2">
          No {status} reports.
        </p>
      ) : null}

      <div className="flex flex-col gap-md">
        {reports.map((r) => (
          <div key={r.id} className="rounded-card border border-border bg-surface p-lg shadow-card">
            <div className="flex items-center justify-between gap-sm">
              <p className="text-body font-semibold text-ink">
                {r.target_type} · {r.reason}
              </p>
              <span className="text-caption text-ink-2">{new Date(r.created_at).toLocaleString()}</span>
            </div>
            <p className="mt-xs text-caption text-ink-2">
              Reported by {r.reporter_name ?? 'unknown'} · author {r.author_name ?? 'unknown'}
            </p>
            {r.detail ? <p className="mt-sm text-body text-ink">{r.detail}</p> : null}
            {r.content ? <p className="mt-xs text-caption italic text-ink-3">"{r.content}"</p> : null}
            {r.reviewer_note ? (
              <p className="mt-sm text-caption text-ink-2">Reviewer note: {r.reviewer_note}</p>
            ) : null}

            {status === 'pending' ? (
              <div className="mt-md flex flex-col gap-sm">
                <textarea
                  value={notes[r.id] ?? ''}
                  onChange={(e) => setNotes((prev) => ({ ...prev, [r.id]: e.target.value }))}
                  placeholder="Reviewer note (optional, visible to other admins)…"
                  className="min-h-[44px] rounded-button border border-border bg-surface p-sm text-body text-ink"
                />
                {r.target_type === 'review' && r.review_id ? (
                  <label className="flex items-center gap-sm text-body text-ink">
                    <input
                      type="checkbox"
                      checked={hideReview[r.id] === true}
                      onChange={(e) => setHideReview((prev) => ({ ...prev, [r.id]: e.target.checked }))}
                    />
                    Hide this review (only applies when actioned)
                  </label>
                ) : null}
                <div className="flex gap-sm">
                  <button
                    type="button"
                    disabled={resolveMutation.isPending || !toReason(reason)}
                    onClick={() => resolveMutation.mutate({ reportId: r.id, next: 'actioned' })}
                    className="rounded-button bg-danger px-md py-sm text-body font-semibold text-white disabled:opacity-50"
                  >
                    Action
                  </button>
                  <button
                    type="button"
                    disabled={resolveMutation.isPending || !toReason(reason)}
                    onClick={() => resolveMutation.mutate({ reportId: r.id, next: 'dismissed' })}
                    className="rounded-button border border-border px-md py-sm text-body text-ink disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {nextCursor && !isLoading ? (
        <button
          type="button"
          onClick={() => setCursor(nextCursor)}
          className="w-fit rounded-button border border-border px-md py-sm text-body text-ink"
        >
          Load more
        </button>
      ) : null}

      {resolveMutation.isError ? (
        <p className="text-body text-danger">{(resolveMutation.error as Error).message}</p>
      ) : null}
    </section>
  );
}
